import { forwardRef, useEffect, useRef, useState, type ReactNode } from 'react';
import { Mic } from 'lucide-react';
import { Button, type ButtonProps } from '../Button/Button';
import { Badge } from '../Badge/Badge';
import { Text } from '../Typography/Text';
import { LiveRegion } from '../LiveRegion/LiveRegion';
import { OverflowMenu, type OverflowMenuItem } from '../OverflowMenu/OverflowMenu';
import { useTranslation } from '../../../i18n/useTranslation';
import './VoiceRecorderButton.css';

/*
 * VoiceRecorderButton / VoiceRecorderBar — an atom per CLAUDE.md §1a rule 5:
 * an accessibility-critical control (recording state machine, keyboard
 * shortcuts, live announcements) needed in 6+ places (capture sheet, page
 * headers, empty states, follow-ups, the pinned recorder bar). Keyboard and
 * ARIA behaviour must exist exactly once. Purely presentational: the parent
 * owns MediaRecorder (hooks/useVoiceRecorder) and every API call.
 */

// ─── Trigger ─────────────────────────────────────────────────────────────────

export interface VoiceRecorderButtonProps extends Omit<ButtonProps, 'children' | 'variant'> {
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Only the microphone icon is shown; `ariaLabel` is then required. */
  iconOnly?: boolean;
  ariaLabel?: string;
  /** Visible label; defaults to "Record note". */
  children?: ReactNode;
  /** Called on activation (alias of onClick without the event). */
  onStart?: () => void;
}

export const VoiceRecorderButton = forwardRef<HTMLButtonElement, VoiceRecorderButtonProps>(function VoiceRecorderButton(
  { variant = 'primary', iconOnly = false, ariaLabel, children, onStart, onClick, className = '', ...rest },
  ref,
) {
  const { t } = useTranslation();
  const label = children ?? t('voice.recorder.start');
  return (
    <Button
      ref={ref}
      variant={variant}
      className={`rg-voice-recorder-button${iconOnly ? ' rg-voice-recorder-button--icon-only' : ''} ${className}`.trim()}
      aria-label={iconOnly ? (ariaLabel ?? String(t('voice.recorder.start'))) : ariaLabel}
      onClick={(event) => {
        onClick?.(event);
        onStart?.();
      }}
      {...rest}
    >
      <span className="rg-voice-recorder-button__content">
        <Mic size={18} aria-hidden="true" focusable="false" />
        {!iconOnly && <span>{label}</span>}
      </span>
    </Button>
  );
});
VoiceRecorderButton.displayName = 'VoiceRecorderButton';

// ─── Level meter (internal) ──────────────────────────────────────────────────

/** Input level 0..1 as a bar. Decorative (aria-hidden); static under reduced motion. */
function VoiceLevelMeter({ level }: { level: number }) {
  const clamped = Math.max(0, Math.min(1, Number.isFinite(level) ? level : 0));
  return (
    <div className="rg-voice-level" aria-hidden="true">
      {/* Dynamic value (the live input level) — the one allowed kind of inline style. */}
      <div className="rg-voice-level__fill" style={{ transform: `scaleX(${clamped})` }} />
    </div>
  );
}

// ─── Recorder bar ────────────────────────────────────────────────────────────

export type VoiceRecorderPhase = 'recording' | 'paused' | 'uploading' | 'processing';

export interface VoiceRecorderBarProps {
  phase: VoiceRecorderPhase;
  elapsedMs: number;
  /** Input level 0..1. */
  level: number;
  /** The level stayed below the threshold for ≥ 3 s. */
  lowSignal?: boolean;
  /** 10 s (or less) of the maximum duration are left. */
  limitWarning?: boolean;
  maxDurationSec?: number;
  /** Credits of one note, shown in the hint ("Max. 3 min · 3 AI credits"). */
  credits?: number;
  /** Name of the recording language (overflow menu item); omitted → no language item. */
  languageLabel?: string;
  onStop: () => void;
  onPause: () => void;
  onResume: () => void;
  onDiscard: () => void;
  onLanguage?: () => void;
  /** Processing state: start the next note. */
  onRecordAnother?: () => void;
  /** Processing state: hide the bar. */
  onClose?: () => void;
  className?: string;
}

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function VoiceRecorderBar({
  phase,
  elapsedMs,
  level,
  lowSignal = false,
  limitWarning = false,
  maxDurationSec = 180,
  credits,
  languageLabel,
  onStop,
  onPause,
  onResume,
  onDiscard,
  onLanguage,
  onRecordAnother,
  onClose,
  className = '',
}: VoiceRecorderBarProps) {
  const { t } = useTranslation();
  const barRef = useRef<HTMLDivElement>(null);
  const stopRef = useRef<HTMLButtonElement>(null);
  const recordAnotherRef = useRef<HTMLButtonElement>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const lowSignalAnnounced = useRef(false);
  const live = phase === 'recording' || phase === 'paused';
  const previousPhase = useRef<VoiceRecorderPhase | null>(null);

  // Focus moves to Stop when recording starts; to "Record another" once processing.
  useEffect(() => {
    const prev = previousPhase.current;
    previousPhase.current = phase;
    if (phase === 'recording' && prev !== 'recording' && prev !== 'paused') {
      stopRef.current?.focus();
      setAnnouncement(String(t('voice.recorder.announceStarted')));
      lowSignalAnnounced.current = false;
    } else if (phase === 'processing' && prev !== 'processing') {
      setConfirmDiscard(false);
      setAnnouncement(String(t('voice.recorder.announceProcessing')));
      recordAnotherRef.current?.focus();
    }
  }, [phase, t]);

  useEffect(() => {
    if (limitWarning && live) setAnnouncement(String(t('voice.recorder.limitWarning')));
  }, [limitWarning, live, t]);

  useEffect(() => {
    if (lowSignal && live && !lowSignalAnnounced.current) {
      lowSignalAnnounced.current = true;
      setAnnouncement(String(t('voice.recorder.lowSignal')));
    }
  }, [lowSignal, live, t]);

  // Escape anywhere in the bar asks to discard (native listener: the bar is a group, not a widget).
  // The overflow menu handles its own Escape first (defaultPrevented).
  useEffect(() => {
    const bar = barRef.current;
    if (!bar || !live) return undefined;
    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      event.preventDefault();
      setConfirmDiscard(true);
    };
    bar.addEventListener('keydown', onKeyDown);
    return () => bar.removeEventListener('keydown', onKeyDown);
  }, [live]);

  const menuItems: OverflowMenuItem[] = live
    ? [
        phase === 'paused'
          ? { id: 'resume', label: String(t('voice.recorder.resume')), onSelect: onResume }
          : { id: 'pause', label: String(t('voice.recorder.pause')), onSelect: onPause },
        ...(languageLabel !== undefined && onLanguage
          ? [{ id: 'language', label: String(t('voice.recorder.language', { language: languageLabel })), onSelect: onLanguage }]
          : []),
        { id: 'discard', label: String(t('voice.recorder.discard')), onSelect: () => setConfirmDiscard(true), tone: 'danger' as const },
      ]
    : [];

  const maxMinutes = Math.round(maxDurationSec / 60);

  return (
    <div
      className={`rg-voice-recorder-bar ${className}`.trim()}
      ref={barRef}
      role="group"
      aria-label={String(t('voice.recorder.barLabel'))}
      data-testid="voice-recorder-bar"
    >
      {live && (
        <>
          <div className="rg-voice-recorder-bar__head">
            <div className="rg-voice-recorder-bar__status">
              <Badge tone="danger">
                {phase === 'paused' ? t('voice.recorder.pausedBadge') : t('voice.recorder.recordingBadge')}
              </Badge>
              <Text as="span" weight="semibold" className="rg-voice-recorder-bar__elapsed">
                {t('voice.recorder.elapsed', { time: formatElapsed(elapsedMs) })}
              </Text>
            </div>
            <OverflowMenu label={String(t('voice.recorder.more'))} items={menuItems} />
          </div>
          <VoiceLevelMeter level={phase === 'paused' ? 0 : level} />
          {lowSignal && (
            <Text as="span" variant="caption" className="rg-voice-recorder-bar__hint">
              {t('voice.recorder.lowSignal')}
            </Text>
          )}
          {confirmDiscard ? (
            <div className="rg-voice-recorder-bar__confirm">
              <Text as="span" weight="medium">{t('voice.recorder.discardConfirm')}</Text>
              <div className="rg-voice-recorder-bar__confirm-actions">
                <Button variant="secondary" onClick={() => setConfirmDiscard(false)}>
                  {t('voice.recorder.keepRecording')}
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    setConfirmDiscard(false);
                    onDiscard();
                  }}
                >
                  {t('voice.recorder.discardConfirmAction')}
                </Button>
              </div>
            </div>
          ) : (
            <Button ref={stopRef} variant="primary" size="lg" fullWidth className="rg-voice-recorder-bar__stop" onClick={onStop}>
              {t('voice.recorder.stop')}
            </Button>
          )}
          <Text as="span" variant="caption" color="muted">
            {credits !== undefined
              ? t('voice.recorder.maxHint', { minutes: maxMinutes, credits })
              : t('voice.recorder.maxHintNoCost', { minutes: maxMinutes })}
          </Text>
          {/* Privacy review D4: not only in the consent dialog, also while recording. */}
          <Text as="span" variant="caption" color="muted">
            {t('voice.recorder.thirdParties')}
          </Text>
        </>
      )}
      {!live && (
        <div className="rg-voice-recorder-bar__processing">
          <Badge tone="info">
            {phase === 'uploading' ? t('voice.recorder.uploading') : t('voice.recorder.processing')}
          </Badge>
          <div className="rg-voice-recorder-bar__processing-actions">
            {onRecordAnother && (
              <Button ref={recordAnotherRef} variant="secondary" onClick={onRecordAnother}>
                <span className="rg-voice-recorder-button__content">
                  <Mic size={18} aria-hidden="true" focusable="false" />
                  <span>{t('voice.recorder.recordAnother')}</span>
                </span>
              </Button>
            )}
            {onClose && (
              <Button variant="ghost" onClick={onClose}>
                {t('voice.recorder.hide')}
              </Button>
            )}
          </div>
        </div>
      )}
      <LiveRegion visuallyHidden>{announcement}</LiveRegion>
    </div>
  );
}
